import React, { useState, useEffect, useCallback } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, Switch, Platform, useWindowDimensions, ActivityIndicator, Alert, Modal } from 'react-native';
import { appConfig, getTwoCardMaxWidth, getCardMargin } from '../../config/configManager';
import { UserAgreementModal } from '../UserAgreementModal';
import { PrivacyPolicyModal } from '../PrivacyPolicyModal';
import { getColor } from '../../config/appConfig';
import { STALENESS_OPTIONS as STALE_OPTS } from '../../utils/milestoneStaleness';
import { WellCheckStorage, WellCheckScheduleDto, VaccineScheduleDto } from '../../storage/WellCheckStorage';
import { useUserStore } from '../../contexts/StoreContext';
import type { AccountUserSettingsDto, AccountUserSettingDto } from '../../types/api/service';

// Available language options
const LANGUAGE_OPTIONS = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  // { code: 'es', name: 'Español', flag: '🇪🇸' },
  // { code: 'fr', name: 'Français', flag: '🇫🇷' },
  // { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  // { code: 'zh', name: '中文', flag: '🇨🇳' },
  // { code: 'pt', name: 'Português', flag: '🇧🇷' },
];

// Available milestone staleness threshold options
const STALENESS_OPTIONS = [
  { value: '1week', label: '1 Week', days: 7 },
  { value: '2weeks', label: '2 Weeks', days: 14 },
  { value: '3weeks', label: '3 Weeks', days: 21 },
  { value: '1month', label: '1 Month', days: 30 },
  { value: '2months', label: '2 Months', days: 60 },
  { value: '3months', label: '3 Months', days: 90 },
];

// Auto sign-out timeout options (web only)
const AUTO_SIGNOUT_KEY = 'auto_signout_timeout';
const AUTO_SIGNOUT_OPTIONS = [
  { value: 'disabled', label: 'Disabled' },
  { value: '15min',    label: '15 Minutes' },
  { value: '30min',    label: '30 Minutes' },
  { value: '1hour',    label: '1 Hour' },
  { value: '2hours',   label: '2 Hours' },
  { value: '4hours',   label: '4 Hours' },
];

interface SettingsViewProps {
  storage?: any;
  apiService?: any;
  contentService?: any;
  useContentStore?: any;
}

export const SettingsView: React.FC<SettingsViewProps> = ({ storage, apiService, contentService, useContentStore }) => {
  const config = appConfig.getConfig();
  
  // State for various settings - initialize from config
  const [selectedLanguage, setSelectedLanguage] = useState(config.localization.languagetype);
  const [milestoneStaleness, setMilestoneStaleness] = useState('1month'); // Default to 1 month
  const [concernFreshness, setConcernFreshness] = useState('1month'); // Default to 1 month
  const [actionFreshness, setActionFreshness] = useState('1month'); // Default to 1 month
  const [isPreferencesDirty, setIsPreferencesDirty] = useState(false);
  const [isSyncingPreferences, setIsSyncingPreferences] = useState(false);
  const userStore = useUserStore();
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [darkModeEnabled, setDarkModeEnabled] = useState(false);
  const [autoBackupEnabled, setAutoBackupEnabled] = useState(false);
  const [analyticsEnabled, setAnalyticsEnabled] = useState(false);
  // UI feature enables — controlled via DebugView > UI Features (default false = hidden)
  const [showPushNotifications, setShowPushNotifications] = useState(false);
  const [showDarkMode, setShowDarkMode] = useState(false);
  const [showAutoBackup, setShowAutoBackup] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [autoSignoutTimeout, setAutoSignoutTimeout] = useState('disabled');
  const [showAgreement, setShowAgreement] = useState(false);
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const [showContactSupport, setShowContactSupport] = useState(false);
  const [wellCheckSchedules, setWellCheckSchedules] = useState<WellCheckScheduleDto[]>([]);
  const [selectedScheduleId, setSelectedScheduleId] = useState<string>('');
  const [vaccineSchedules, setVaccineSchedules] = useState<VaccineScheduleDto[]>([]);
  const [selectedVaccineScheduleId, setSelectedVaccineScheduleId] = useState<string>('');
  const [vaccineInfoEnabled, setVaccineInfoEnabled] = useState(true);
  const [trackPrescriptions, setTrackPrescriptions] = useState(true);
  const [trackReferrals, setTrackReferrals] = useState(true);
  const [trackHeightWeight, setTrackHeightWeight] = useState(true);
  const [measurementUnits, setMeasurementUnits] = useState<'english' | 'metric'>('english');
  
  // Load milestone staleness setting from storage on component mount
  useEffect(() => {
    const loadMilestoneStaleness = async () => {
      if (storage) {
        try {
          const storedValue = await storage.getItem('milestone_staleness_threshold');
          if (storedValue && STALENESS_OPTIONS.find(opt => opt.value === storedValue)) {
            setMilestoneStaleness(storedValue);
          }
        } catch (error) {
          console.warn('Failed to load milestone staleness setting:', error);
        }
      }
    };
    loadMilestoneStaleness();
  }, [storage]);

  // Load concern freshness threshold setting from storage
  useEffect(() => {
    const loadConcernFreshness = async () => {
      if (storage) {
        try {
          const storedValue = await storage.getItem('concern_freshness_threshold');
          if (storedValue && STALE_OPTS.find(opt => opt.value === storedValue)) {
            setConcernFreshness(storedValue);
          }
        } catch (error) {
          console.warn('Failed to load concern freshness setting:', error);
        }
      }
    };
    loadConcernFreshness();
  }, [storage]);

  // Load action freshness threshold setting from storage
  useEffect(() => {
    const loadActionFreshness = async () => {
      if (storage) {
        try {
          const storedValue = await storage.getItem('action_freshness_threshold');
          if (storedValue && STALE_OPTS.find(opt => opt.value === storedValue)) {
            setActionFreshness(storedValue);
          }
        } catch (error) {
          console.warn('Failed to load action freshness setting:', error);
        }
      }
    };
    loadActionFreshness();
  }, [storage]);

  // Load WellCheck schedules from storage and persisted selection
  useEffect(() => {
    const loadWellCheckData = async () => {
      const globalStorage = storage || (globalThis as any).__childNavStorage;
      if (!globalStorage) return;
      try {
        const wellCheckStorage = new WellCheckStorage(globalStorage);
        const lists = await wellCheckStorage.getAllWellCheckDefs();
        const allSchedules = lists.flatMap(l => l.schedules);
        setWellCheckSchedules(allSchedules);
        const savedId = await globalStorage.getItem('wellcheck_selected_schedule_id');
        if (savedId && allSchedules.find(s => s.scheduleId === savedId)) {
          setSelectedScheduleId(savedId);
        } else if (allSchedules.length > 0) {
          setSelectedScheduleId(allSchedules[0].scheduleId);
        }
        const savedVaccine = await globalStorage.getItem('wellcheck_vaccine_info_enabled');
        if (savedVaccine !== null) setVaccineInfoEnabled(savedVaccine === 'true');
        const savedTrackRx = await globalStorage.getItem('wellcheck_track_prescriptions');
        if (savedTrackRx !== null) setTrackPrescriptions(savedTrackRx === 'true');
        const savedTrackRef = await globalStorage.getItem('wellcheck_track_referrals');
        if (savedTrackRef !== null) setTrackReferrals(savedTrackRef === 'true');
        const savedTrackHW = await globalStorage.getItem('wellcheck_track_height_weight');
        if (savedTrackHW !== null) setTrackHeightWeight(savedTrackHW === 'true');
        const savedUnits = await globalStorage.getItem('wellcheck_measurement_units');
        if (savedUnits === 'metric' || savedUnits === 'english') setMeasurementUnits(savedUnits);
        const allVacSchedules = lists.flatMap(l => l.vacSchedules);
        setVaccineSchedules(allVacSchedules);
        const savedVsId = await globalStorage.getItem('wellcheck_selected_vaccine_schedule_id');
        if (savedVsId && allVacSchedules.find(s => s.scheduleId === savedVsId)) {
          setSelectedVaccineScheduleId(savedVsId);
        } else if (allVacSchedules.length > 0) {
          setSelectedVaccineScheduleId(allVacSchedules[0].scheduleId);
        }
      } catch (error) {
        console.warn('Failed to load WellCheck settings:', error);
      }
    };
    loadWellCheckData();
  }, [storage]);

  // Load Preferences feature enables set by DebugView
  useEffect(() => {
    const loadFeatureEnables = async () => {
      if (!storage) return;
      try {
        const pushSetting = await storage.getItem('debug_enable_push_notifications');
        if (pushSetting === 'true') setShowPushNotifications(true);
        const darkSetting = await storage.getItem('debug_enable_dark_mode');
        if (darkSetting === 'true') setShowDarkMode(true);
        const backupSetting = await storage.getItem('debug_enable_auto_backup');
        if (backupSetting === 'true') setShowAutoBackup(true);
        const analyticsSetting = await storage.getItem('debug_enable_analytics');
        if (analyticsSetting === 'true') setShowAnalytics(true);
      } catch (error) {
        console.warn('Failed to load Preferences feature enables:', error);
      }
    };
    loadFeatureEnables();
  }, [storage]);

  // Load auto sign-out timeout setting
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    const loadAutoSignout = async () => {
      // Try abstract storage first; fall back to localStorage (set by the hook)
      let stored: string | null = null;
      if (storage) {
        try { stored = await storage.getItem(AUTO_SIGNOUT_KEY); } catch {}
      }
      if (!stored) {
        try { stored = typeof window !== 'undefined' ? localStorage.getItem(AUTO_SIGNOUT_KEY) : null; } catch {}
      }
      if (stored && AUTO_SIGNOUT_OPTIONS.find(o => o.value === stored)) {
        setAutoSignoutTimeout(stored);
      }
    };
    loadAutoSignout();
  }, [storage]);

  // Persist dirty flag across view changes — load from storage on mount
  useEffect(() => {
    const loadDirtyFlag = async () => {
      const gs = storage || (globalThis as any).__childNavStorage;
      if (!gs) return;
      try {
        const dirty = await gs.getItem('preferences_dirty_flag');
        if (dirty === 'true') setIsPreferencesDirty(true);
      } catch {}
    };
    loadDirtyFlag();
  }, [storage]);

  // Defaults used for dirty comparison (a pref is dirty when it differs from default)
  const PREF_DEFAULTS: Record<string, string> = {
    track_prescriptions: 'true',
    track_referrals: 'true',
    track_vaccinations: 'true',
    track_height_weight: 'true',
    measurement_units: 'english',
    language: config.localization.languagetype || 'en',
    milestone_staleness_threshold: '1month',
    concern_freshness_threshold: '1month',
    action_freshness_threshold: '1month',
    auto_signout_timeout: 'disabled',
  };

  // Function to update milestone staleness setting
  const updateMilestoneStaleness = async (newValue: string) => {
    setMilestoneStaleness(newValue);
    setIsPreferencesDirty(true);
    if (storage) {
      try {
        await storage.setItem('milestone_staleness_threshold', newValue);
        await storage.setItem('preferences_dirty_flag', 'true');
        console.log(`Milestone staleness threshold changed to: ${newValue}`);
      } catch (error) {
        console.warn('Failed to save milestone staleness setting:', error);
      }
    }
  };

  // Function to update concern freshness threshold setting
  const updateConcernFreshness = async (newValue: string) => {
    setConcernFreshness(newValue);
    setIsPreferencesDirty(true);
    if (storage) {
      try {
        await storage.setItem('concern_freshness_threshold', newValue);
        await storage.setItem('preferences_dirty_flag', 'true');
        console.log(`Concern freshness threshold changed to: ${newValue}`);
      } catch (error) {
        console.warn('Failed to save concern freshness setting:', error);
      }
    }
  };

  // Function to update action freshness threshold setting
  const updateActionFreshness = async (newValue: string) => {
    setActionFreshness(newValue);
    setIsPreferencesDirty(true);
    if (storage) {
      try {
        await storage.setItem('action_freshness_threshold', newValue);
        await storage.setItem('preferences_dirty_flag', 'true');
        console.log(`Action freshness threshold changed to: ${newValue}`);
      } catch (error) {
        console.warn('Failed to save action freshness setting:', error);
      }
    }
  };

  // Sync all preferences to backend as AccountUserSettingsDto
  const syncPreferences = useCallback(async () => {
    console.log('🔄 syncPreferences called, apiService:', !!apiService, 'storage:', !!storage);
    if (!apiService || !storage) {
      console.warn('SettingsView: syncPreferences — apiService or storage missing');
      Alert.alert('Sync Error', 'API service or storage not available.');
      return;
    }
    setIsSyncingPreferences(true);
    try {
      // Build settings array with all current preference values
      const allSettings: AccountUserSettingDto[] = [
        { Name: 'track_prescriptions',          Value: trackPrescriptions    ? 'true' : 'false' },
        { Name: 'track_referrals',               Value: trackReferrals        ? 'true' : 'false' },
        { Name: 'track_vaccinations',            Value: vaccineInfoEnabled    ? 'true' : 'false' },
        { Name: 'track_height_weight',           Value: trackHeightWeight     ? 'true' : 'false' },
        { Name: 'measurement_units',             Value: measurementUnits },
        { Name: 'language',                      Value: selectedLanguage },
        { Name: 'milestone_staleness_threshold', Value: milestoneStaleness },
        { Name: 'concern_freshness_threshold',   Value: concernFreshness },
        { Name: 'action_freshness_threshold',    Value: actionFreshness },
        ...(Platform.OS === 'web'
          ? [{ Name: 'auto_signout_timeout', Value: autoSignoutTimeout }]
          : []),
      ];
      if (selectedScheduleId) {
        allSettings.push({ Name: 'wellcheck_schedule_id',        Value: selectedScheduleId });
      }
      if (selectedVaccineScheduleId) {
        allSettings.push({ Name: 'vaccine_schedule_id', Value: selectedVaccineScheduleId });
      }

      // Resolve UserId from userStore
      const userState = userStore && typeof (userStore as any).getState === 'function'
        ? (userStore as any).getState()
        : null;
      const userId = userState?.session?.user?.userId
        || userState?.session?.user?.id
        || userState?.session?.familyAccount?.familyId
        || '0';

      const payload: AccountUserSettingsDto = { UserId: userId, settings: allSettings };
      const request = {
        RequestType: 'account_put_settings',
        Payload: JSON.stringify(payload),
      };
      const response = await apiService.processServiceRequestWithRefresh(request, 'sync', {
        userStore,
        storage,
      });
      if (!response?.success) {
        throw new Error(response?.message || 'Backend returned an error');
      }
      setIsPreferencesDirty(false);
      try { await storage.removeItem('preferences_dirty_flag'); } catch {}
      console.log('\u2705 Preferences synced successfully');
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);
      Alert.alert('Sync Failed', `Could not sync preferences: ${msg}`);
    } finally {
      setIsSyncingPreferences(false);
    }
  }, [
    trackPrescriptions, trackReferrals, vaccineInfoEnabled, trackHeightWeight,
    measurementUnits, selectedLanguage, milestoneStaleness, concernFreshness,
    actionFreshness, autoSignoutTimeout, selectedScheduleId, selectedVaccineScheduleId,
    apiService, storage, userStore,
  ]);
  
  // Function to update language in config
  const updateLanguage = async (newLanguage: string) => {
    setSelectedLanguage(newLanguage);
    setIsPreferencesDirty(true);
    const globalStorage = storage || (globalThis as any).__childNavStorage;
    if (globalStorage) {
      try {
        await globalStorage.setItem('language', newLanguage);
        await globalStorage.setItem('preferences_dirty_flag', 'true');
      } catch {}
    }
    console.log(`Language changed to: ${newLanguage}`);
  };

  const updateSelectedSchedule = async (scheduleId: string) => {
    setSelectedScheduleId(scheduleId);
    setIsPreferencesDirty(true);
    const globalStorage = storage || (globalThis as any).__childNavStorage;
    if (globalStorage) {
      try {
        await globalStorage.setItem('wellcheck_selected_schedule_id', scheduleId);
        await globalStorage.setItem('preferences_dirty_flag', 'true');
      } catch {}
    }
  };

  const updateVaccineInfoEnabled = async (value: boolean) => {
    setVaccineInfoEnabled(value);
    setIsPreferencesDirty(true);
    const globalStorage = storage || (globalThis as any).__childNavStorage;
    if (globalStorage) {
      try {
        await globalStorage.setItem('wellcheck_vaccine_info_enabled', value ? 'true' : 'false');
        await globalStorage.setItem('preferences_dirty_flag', 'true');
      } catch {}
    }
  };

  const updateTrackHeightWeight = async (value: boolean) => {
    setTrackHeightWeight(value);
    setIsPreferencesDirty(true);
    const globalStorage = storage || (globalThis as any).__childNavStorage;
    if (globalStorage) {
      try {
        await globalStorage.setItem('wellcheck_track_height_weight', value ? 'true' : 'false');
        await globalStorage.setItem('preferences_dirty_flag', 'true');
      } catch {}
    }
  };

  const updateTrackPrescriptions = async (value: boolean) => {
    setTrackPrescriptions(value);
    setIsPreferencesDirty(true);
    const globalStorage = storage || (globalThis as any).__childNavStorage;
    if (globalStorage) {
      try {
        await globalStorage.setItem('wellcheck_track_prescriptions', value ? 'true' : 'false');
        await globalStorage.setItem('preferences_dirty_flag', 'true');
      } catch {}
    }
  };

  const updateTrackReferrals = async (value: boolean) => {
    setTrackReferrals(value);
    setIsPreferencesDirty(true);
    const globalStorage = storage || (globalThis as any).__childNavStorage;
    if (globalStorage) {
      try {
        await globalStorage.setItem('wellcheck_track_referrals', value ? 'true' : 'false');
        await globalStorage.setItem('preferences_dirty_flag', 'true');
      } catch {}
    }
  };

  const updateSelectedVaccineSchedule = async (scheduleId: string) => {
    setSelectedVaccineScheduleId(scheduleId);
    setIsPreferencesDirty(true);
    const globalStorage = storage || (globalThis as any).__childNavStorage;
    if (globalStorage) {
      try {
        await globalStorage.setItem('wellcheck_selected_vaccine_schedule_id', scheduleId);
        await globalStorage.setItem('preferences_dirty_flag', 'true');
      } catch {}
    }
  };

  const updateMeasurementUnits = async (value: 'english' | 'metric') => {
    setMeasurementUnits(value);
    setIsPreferencesDirty(true);
    const globalStorage = storage || (globalThis as any).__childNavStorage;
    if (globalStorage) {
      try {
        await globalStorage.setItem('wellcheck_measurement_units', value);
        await globalStorage.setItem('preferences_dirty_flag', 'true');
      } catch {}
    }
  };

  // Function to update auto sign-out timeout (web only)
  const updateAutoSignoutTimeout = async (newValue: string) => {
    setAutoSignoutTimeout(newValue);
    // Write to abstract storage
    if (storage) {
      try { await storage.setItem(AUTO_SIGNOUT_KEY, newValue); } catch {}
    }
    // Also write directly to localStorage so the hook can read it synchronously
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      try {
        localStorage.setItem(AUTO_SIGNOUT_KEY, newValue);
        // Notify AutoSignOutManager in the same tab to re-arm
        window.dispatchEvent(new Event('autoSignOutSettingChanged'));
      } catch {}
    }
    setIsPreferencesDirty(true);
    if (storage) { try { await storage.setItem('preferences_dirty_flag', 'true'); } catch {} }
    console.log(`Auto sign-out timeout changed to: ${newValue}`);
  };

  const { width: screenWidth } = useWindowDimensions();
  const isWide = screenWidth >= 700;

  const renderSettingSection = (title: string, children: React.ReactNode, extraStyle?: object) => (
    <View style={[styles.section, extraStyle]}>
      <Text style={[styles.sectionTitle, styles.sectionTitleStandalone]}>{title}</Text>
      {children}
    </View>
  );

  const renderPreferencesSection = (children: React.ReactNode, extraStyle?: object) => (
    <View style={[styles.section, extraStyle]}>
      <View style={styles.sectionTitleRow}>
        <Text style={styles.sectionTitle}>Preferences</Text>
        {isPreferencesDirty && (
          <TouchableOpacity
            style={[styles.prefSyncIconButton, isSyncingPreferences && styles.prefSyncIconButtonDisabled]}
            onPress={syncPreferences}
            disabled={isSyncingPreferences}
          >
            {isSyncingPreferences
              ? <ActivityIndicator size="small" color="#3498db" />
              : <Text style={styles.prefSyncIconText}>Save</Text>
            }
          </TouchableOpacity>
        )}
      </View>
      {children}
    </View>
  );

  const renderSettingItem = (
    title: string,
    description: string,
    value: boolean,
    onValueChange: (value: boolean) => void,
    icon?: string
  ) => (
    <View style={styles.settingItem}>
      <View style={styles.settingContent}>
        {icon && <Text style={styles.settingIcon}>{icon}</Text>}
        <View style={styles.settingText}>
          <Text style={styles.settingTitle}>{title}</Text>
          <Text style={styles.settingDescription}>{description}</Text>
        </View>
      </View>
      <Switch
        value={value}
        onValueChange={onValueChange}
        trackColor={{
          false: '#e1e8ed',
          true: getColor(config, 'lightBlue')
        }}
        thumbColor={value ? getColor(config, 'blue') : '#f4f3f4'}
      />
    </View>
  );

  const renderInfoItem = (title: string, value: string, icon?: string) => (
    <View style={styles.infoItem}>
      {icon && <Text style={styles.settingIcon}>{icon}</Text>}
      <View style={styles.settingText}>
        <Text style={styles.settingTitle}>{title}</Text>
        <Text style={styles.infoValue}>{value}</Text>
      </View>
    </View>
  );

  const renderActionItem = (title: string, description: string, onPress: () => void, icon?: string) => (
    <TouchableOpacity style={styles.actionItem} onPress={onPress}>
      <View style={styles.settingContent}>
        {icon && <Text style={styles.settingIcon}>{icon}</Text>}
        <View style={styles.settingText}>
          <Text style={styles.settingTitle}>{title}</Text>
          <Text style={styles.settingDescription}>{description}</Text>
        </View>
      </View>
      <Text style={styles.actionArrow}>›</Text>
    </TouchableOpacity>
  );

  const renderLanguageSelector = () => {
    const selectedLanguageOption = LANGUAGE_OPTIONS.find(lang => lang.code === selectedLanguage) || LANGUAGE_OPTIONS[0];
    
    return (
      <TouchableOpacity 
        style={styles.languageSelector}
        onPress={() => {
          // For now, cycle through languages. In a real app, this would show a picker modal
          const currentIndex = LANGUAGE_OPTIONS.findIndex(lang => lang.code === selectedLanguage);
          const nextIndex = (currentIndex + 1) % LANGUAGE_OPTIONS.length;
          updateLanguage(LANGUAGE_OPTIONS[nextIndex].code);
        }}
      >
        <View style={styles.settingContent}>
          <Text style={styles.settingIcon}>🌐</Text>
          <View style={styles.settingText}>
            <Text style={styles.settingTitle}>Language</Text>
            <Text style={styles.settingDescription}>Choose your preferred language</Text>
          </View>
        </View>
        <View style={styles.languageValue}>
          <Text style={styles.languageName}>{selectedLanguageOption.name}</Text>
          <Text style={styles.actionArrow}>›</Text>
        </View>
      </TouchableOpacity>
    );
  };

  const renderMilestoneStalnessSelector = () => {
    const selectedOption = STALENESS_OPTIONS.find(opt => opt.value === milestoneStaleness) || STALENESS_OPTIONS[1];
    
    return (
      <TouchableOpacity 
        style={styles.languageSelector}
        onPress={() => {
          const currentIndex = STALENESS_OPTIONS.findIndex(opt => opt.value === milestoneStaleness);
          const nextIndex = (currentIndex + 1) % STALENESS_OPTIONS.length;
          updateMilestoneStaleness(STALENESS_OPTIONS[nextIndex].value);
        }}
      >
        <View style={styles.settingContent}>
          <Text style={styles.settingIcon}>⏱️</Text>
          <View style={styles.settingText}>
            <Text style={styles.settingTitle}>Milestones Update Alert</Text>
            <Text style={styles.settingDescription}>When to flag milestone results as out-of-date</Text>
          </View>
        </View>
        <View style={styles.languageValue}>
          <Text style={styles.languageName}>{selectedOption.label}</Text>
          <Text style={styles.actionArrow}>›</Text>
        </View>
      </TouchableOpacity>
    );
  };

  const renderConcernFreshnessSelector = () => {
    const selectedOption = STALE_OPTS.find(opt => opt.value === concernFreshness) || STALE_OPTS[1];
    return (
      <TouchableOpacity
        style={styles.languageSelector}
        onPress={() => {
          const currentIndex = STALE_OPTS.findIndex(opt => opt.value === concernFreshness);
          const nextIndex = (currentIndex + 1) % STALE_OPTS.length;
          updateConcernFreshness(STALE_OPTS[nextIndex].value);
        }}
      >
        <View style={styles.settingContent}>
          <Text style={styles.settingIcon}>🔍</Text>
          <View style={styles.settingText}>
            <Text style={styles.settingTitle}>Concerns Update Alert</Text>
            <Text style={styles.settingDescription}>When to flag concern review as out-of-date</Text>
          </View>
        </View>
        <View style={styles.languageValue}>
          <Text style={styles.languageName}>{selectedOption.label}</Text>
          <Text style={styles.actionArrow}>›</Text>
        </View>
      </TouchableOpacity>
    );
  };

  const renderActionFreshnessSelector = () => {
    const selectedOption = STALE_OPTS.find(opt => opt.value === actionFreshness) || STALE_OPTS[1];
    return (
      <TouchableOpacity
        style={styles.languageSelector}
        onPress={() => {
          const currentIndex = STALE_OPTS.findIndex(opt => opt.value === actionFreshness);
          const nextIndex = (currentIndex + 1) % STALE_OPTS.length;
          updateActionFreshness(STALE_OPTS[nextIndex].value);
        }}
      >
        <View style={styles.settingContent}>
          <Text style={styles.settingIcon}>⚡</Text>
          <View style={styles.settingText}>
            <Text style={styles.settingTitle}>Actions Update Alert</Text>
            <Text style={styles.settingDescription}>When to flag action review as out-of-date</Text>
          </View>
        </View>
        <View style={styles.languageValue}>
          <Text style={styles.languageName}>{selectedOption.label}</Text>
          <Text style={styles.actionArrow}>›</Text>
        </View>
      </TouchableOpacity>
    );
  };

  const renderWellCheckScheduleSelector = () => {
    if (wellCheckSchedules.length === 0) return null;
    const selectedSchedule = wellCheckSchedules.find(s => s.scheduleId === selectedScheduleId) ?? wellCheckSchedules[0];
    return (
      <TouchableOpacity
        style={styles.languageSelector}
        onPress={() => {
          const currentIndex = wellCheckSchedules.findIndex(s => s.scheduleId === selectedScheduleId);
          const nextIndex = (currentIndex + 1) % wellCheckSchedules.length;
          updateSelectedSchedule(wellCheckSchedules[nextIndex].scheduleId);
        }}
      >
        <View style={styles.settingContent}>
          <Text style={styles.settingIcon}>🩺</Text>
          <View style={styles.settingText}>
            <Text style={styles.settingTitle}>Well-Check Schedule</Text>
            <Text style={styles.settingDescription}>Select the well-child visit schedule to follow</Text>
          </View>
        </View>
        <View style={styles.languageValue}>
          <Text style={styles.languageName}>{selectedSchedule?.name || '—'}</Text>
          <Text style={styles.actionArrow}>›</Text>
        </View>
      </TouchableOpacity>
    );
  };

  const renderVaccineScheduleSelector = () => {
    if (vaccineSchedules.length === 0) return null;
    const selectedVacSchedule = vaccineSchedules.find(s => s.scheduleId === selectedVaccineScheduleId) ?? vaccineSchedules[0];
    return (
      <TouchableOpacity
        style={styles.languageSelector}
        onPress={() => {
          const currentIndex = vaccineSchedules.findIndex(s => s.scheduleId === selectedVaccineScheduleId);
          const nextIndex = (currentIndex + 1) % vaccineSchedules.length;
          updateSelectedVaccineSchedule(vaccineSchedules[nextIndex].scheduleId);
        }}
      >
        <View style={styles.settingContent}>
          <Text style={styles.settingIcon}>💉</Text>
          <View style={styles.settingText}>
            <Text style={styles.settingTitle}>Vaccination Schedule</Text>
            <Text style={styles.settingDescription}>Select the vaccination schedule to follow</Text>
          </View>
        </View>
        <View style={styles.languageValue}>
          <Text style={styles.languageName}>{selectedVacSchedule?.name || '—'}</Text>
          <Text style={styles.actionArrow}>›</Text>
        </View>
      </TouchableOpacity>
    );
  };

  const renderMeasurementUnitsSelector = () => (
    <TouchableOpacity
      style={styles.languageSelector}
      onPress={() => updateMeasurementUnits(measurementUnits === 'english' ? 'metric' : 'english')}
    >
      <View style={styles.settingContent}>
        <Text style={styles.settingIcon}>📏</Text>
        <View style={styles.settingText}>
          <Text style={styles.settingTitle}>Measurement Units</Text>
          <Text style={styles.settingDescription}>Units used for height and weight tracking</Text>
        </View>
      </View>
      <View style={styles.languageValue}>
        <Text style={styles.languageName}>{measurementUnits === 'english' ? 'English (in / lb)' : 'Metric (cm / kg)'}</Text>
        <Text style={styles.actionArrow}>›</Text>
      </View>
    </TouchableOpacity>
  );

  const renderAutoSignoutSelector = () => {
    const selectedOption = AUTO_SIGNOUT_OPTIONS.find(o => o.value === autoSignoutTimeout) || AUTO_SIGNOUT_OPTIONS[0];
    return (
      <TouchableOpacity
        style={styles.languageSelector}
        onPress={() => {
          const currentIndex = AUTO_SIGNOUT_OPTIONS.findIndex(o => o.value === autoSignoutTimeout);
          const nextIndex = (currentIndex + 1) % AUTO_SIGNOUT_OPTIONS.length;
          updateAutoSignoutTimeout(AUTO_SIGNOUT_OPTIONS[nextIndex].value);
        }}
      >
        <View style={styles.settingContent}>
          <Text style={styles.settingIcon}>🔒</Text>
          <View style={styles.settingText}>
            <Text style={styles.settingTitle}>Auto Sign-Out</Text>
            <Text style={styles.settingDescription}>Sign out automatically after this period of inactivity</Text>
          </View>
        </View>
        <View style={styles.languageValue}>
          <Text style={styles.languageName}>{selectedOption.label}</Text>
          <Text style={styles.actionArrow}>›</Text>
        </View>
      </TouchableOpacity>
    );
  };

  return (
    <View style={styles.backgroundContainer}>
      <ScrollView style={styles.container} contentContainerStyle={styles.contentContainer}>
        <View style={[styles.header, { maxWidth: getTwoCardMaxWidth(), alignSelf: 'center', width: '100%' }]}>
          <Text style={styles.title}>Settings</Text>
          <Text style={styles.subtitle}>Customize your Child Nav experience</Text>
        </View>
        <View style={{ maxWidth: getTwoCardMaxWidth(), alignSelf: 'center', width: '100%', paddingHorizontal: getCardMargin() }}>
        <View style={{ flexDirection: isWide ? 'row' : 'column', gap: 16, alignItems: isWide ? 'flex-start' : 'stretch' }}>
      {/* Preferences */}
      {renderPreferencesSection((
        <>
          {renderSettingItem(
            'Track Vaccinations',
            'Show vaccine recommendations in well-child visit schedules',
            vaccineInfoEnabled,
            updateVaccineInfoEnabled,
            '💉'
          )}
          {renderSettingItem(
            'Track Height & Weight',
            'Record and track height and weight at well-child visits',
            trackHeightWeight,
            updateTrackHeightWeight,
            '📊'
          )}
          {renderSettingItem(
            'Track Prescriptions',
            'Record and manage prescriptions issued at well-child visits',
            trackPrescriptions,
            updateTrackPrescriptions,
            '📝'
          )}
          {renderSettingItem(
            'Track Referrals',
            'Record and manage specialist referrals from well-child visits',
            trackReferrals,
            updateTrackReferrals,
            '👥'
          )}
          {renderWellCheckScheduleSelector()}
          {renderVaccineScheduleSelector()}
          {renderMeasurementUnitsSelector()}
          {renderLanguageSelector()}
          {renderMilestoneStalnessSelector()}
          {renderConcernFreshnessSelector()}
          {renderActionFreshnessSelector()}
          {Platform.OS === 'web' && renderAutoSignoutSelector()}
          {showPushNotifications && renderSettingItem(
            'Push Notifications',
            'Receive milestone reminders and updates',
            notificationsEnabled,
            setNotificationsEnabled,
            '🔔'
          )}
          {showDarkMode && renderSettingItem(
            'Dark Mode',
            'Use dark theme for better low-light viewing',
            darkModeEnabled,
            setDarkModeEnabled,
            '🌙'
          )}
          {showAutoBackup && renderSettingItem(
            'Auto Backup',
            'Automatically backup data to cloud storage',
            autoBackupEnabled,
            setAutoBackupEnabled,
            '☁️'
          )}
          {showAnalytics && renderSettingItem(
            'Analytics',
            'Help improve the app by sharing usage data',
            analyticsEnabled,
            setAnalyticsEnabled,
            '📊'
          )}
        </>
      ), { flex: isWide ? 1 : undefined, marginHorizontal: 0, marginTop: 0 })}

      {/* Support + App Information stacked on the right */}
      <View style={{ flex: isWide ? 1 : undefined, flexDirection: 'column', gap: 16 }}>
        {/* Support */}
        {renderSettingSection('Support', (
          <>
            {renderActionItem(
              'Contact Support',
              'Get in touch with our support team',
              () => setShowContactSupport(true),
              '📧'
            )}
            {renderActionItem(
              'Privacy Policy',
              'Read our privacy policy',
              () => setShowPrivacyPolicy(true),
              '🔒'
            )}
            {renderActionItem(
              'Terms of Service',
              'Read our terms of service',
              () => setShowAgreement(true),
              '📋'
            )}
          </>
        ), { marginHorizontal: 0, marginTop: 0 })}

        {/* App Information */}
        {renderSettingSection('App Information', (
          <>
            {renderInfoItem('Version', config.appVersion, '📱')}
            {renderInfoItem('Build', config.buildNumber, '🔧')}
            {renderInfoItem('Environment', config.environment, '🌍')}
            {renderInfoItem('Tier', config.tier === 'premium' ? 'Premium' : 'Standard', '⭐')}
          </>
        ), { marginHorizontal: 0, marginTop: 0 })}
      </View>
        </View>
        <View style={styles.footer}>
          <Text style={styles.footerText}>
            ChildNav • Made with ❤️ for families
          </Text>
        </View>
        </View>
      </ScrollView>
      <UserAgreementModal visible={showAgreement} onClose={() => setShowAgreement(false)} />
      <PrivacyPolicyModal visible={showPrivacyPolicy} onClose={() => setShowPrivacyPolicy(false)} />
      <Modal visible={showContactSupport} transparent animationType="fade" onRequestClose={() => setShowContactSupport(false)}>
        <View style={styles.contactSupportBackdrop}>
          <View style={styles.contactSupportCard}>
            <Text style={styles.contactSupportTitle}>Contact Support</Text>
            <Text style={styles.contactSupportBody}>
              Need help? Reach out to our support team and we'll get back to you as soon as possible.
            </Text>
            <Text style={styles.contactSupportEmail}>📧 support@childnav.com</Text>
            <TouchableOpacity style={styles.contactSupportClose} onPress={() => setShowContactSupport(false)}>
              <Text style={styles.contactSupportCloseText}>Close</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  backgroundContainer: {
    flex: 1,
    backgroundColor: '#3b82f6',
  },
  container: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  contentContainer: {
    backgroundColor: '#3b82f6',
    paddingVertical: 16,
  },
  header: {
    backgroundColor: 'transparent',
    padding: 24,
    borderBottomWidth: 0,
  },
  title: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#ffffff',
    marginBottom: 8,
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 0, height: 2 },
    textShadowRadius: 4,
  },
  subtitle: {
    fontSize: 16,
    color: '#e2e8f0',
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 2,
  },
  section: {
    backgroundColor: '#ffffff',
    marginHorizontal: 16,
    marginTop: 16,
    paddingVertical: 16,
    borderRadius: 12,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  sectionTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    marginBottom: 12,
  },
  prefSyncIconButton: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 6,
    marginRight: 4,
  },
  prefSyncIconButtonDisabled: {
    opacity: 0.5,
  },
  prefSyncIconText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#3498db',
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#2c3e50',
    paddingHorizontal: 0,
    marginBottom: 0,
  },
  sectionTitleStandalone: {
    paddingHorizontal: 20,
    marginBottom: 12,
  },
  settingItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#f1f2f6',
  },
  settingContent: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  settingIcon: {
    fontSize: 20,
    marginRight: 12,
    width: 24,
    textAlign: 'center',
  },
  settingText: {
    flex: 1,
  },
  settingTitle: {
    fontSize: 16,
    fontWeight: '500',
    color: '#2c3e50',
    marginBottom: 2,
  },
  settingDescription: {
    fontSize: 14,
    color: '#7f8c8d',
  },
  infoItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#f1f2f6',
  },
  infoValue: {
    fontSize: 14,
    color: '#3498db',
    fontWeight: '500',
  },
  actionItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#f1f2f6',
  },
  actionArrow: {
    fontSize: 18,
    color: '#bdc3c7',
    fontWeight: 'bold',
  },
  languageSelector: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#f1f2f6',
  },
  languageValue: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  languageFlag: {
    fontSize: 18,
  },
  languageName: {
    fontSize: 14,
    color: '#3498db',
    fontWeight: '500',
  },
  footer: {
    padding: 20,
    alignItems: 'center',
    marginBottom: 20,
  },
  footerText: {
    fontSize: 14,
    color: '#e2e8f0',
    textAlign: 'center',
    lineHeight: 20,
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 2,
  },
  contactSupportBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  contactSupportCard: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 24,
    width: '100%',
    maxWidth: 400,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 12,
    elevation: 8,
  },
  contactSupportTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: '#1e3a5f',
    marginBottom: 12,
  },
  contactSupportBody: {
    fontSize: 15,
    color: '#475569',
    lineHeight: 22,
    marginBottom: 16,
  },
  contactSupportEmail: {
    fontSize: 15,
    color: '#3b82f6',
    fontWeight: '600',
    marginBottom: 24,
  },
  contactSupportClose: {
    backgroundColor: '#3b82f6',
    borderRadius: 8,
    paddingVertical: 12,
    alignItems: 'center',
  },
  contactSupportCloseText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
});